import React, { useEffect, useMemo, useState } from "react";
import { Box, Card, CardContent, CircularProgress, Grid, Typography } from "@material-ui/core";
import PageTitle from "../../components/PageTitle/PageTitle";
import HandbookService from "./Locality/Service/handbookService";
import { formatDate } from "../../utils/dateFormat";

/** Plain-text / API date values → the school's configured date format. */
function formatHandbookDateDisplay(value) {
  if (value === null || value === undefined || value === "") return null;
  return formatDate(value) || null;
}

/** Field keys stored as dates in handbook JSON (display only; school editor still uses yyyy-mm-dd inputs). */
const HANDBOOK_DATE_FIELD_KEYS = new Set([
  "winterVacationFrom",
  "winterVacationTo",
  "summerVacationFrom",
  "summerVacationTo",
  "paymentScheduleDate",
]);

function isFilled(v) {
  if (v === null || v === undefined) return false;
  if (Array.isArray(v)) return v.some((item) => isFilled(item));
  if (typeof v === "string") return v.trim().length > 0;
  if (v instanceof Date) return !Number.isNaN(v.getTime());
  return true;
}

function Field({ label, value, fieldKey }) {
  if (!isFilled(value)) return null;
  const isExamTerms =
    label.toLowerCase().includes("exam") &&
    Array.isArray(value) &&
    value.every((t) => t && typeof t === "object");
  const isStringList = Array.isArray(value) && value.every((item) => typeof item === "string" || item === null || item === undefined);
  const isImageUrl =
    label.toLowerCase().includes("photo") &&
    typeof value === "string" &&
    (value.startsWith("http://") || value.startsWith("https://"));
  return (
    <Box mb={1.5}>
      <Typography variant="subtitle2" style={{ fontWeight: 700, color: "#203040" }}>
        {label}
      </Typography>
      {isExamTerms ? (
        <Box mt={1}>
          {value
            .filter((t) => isFilled(t?.title) || isFilled(t?.fromDate) || isFilled(t?.toDate))
            .map((t, idx) => (
              <Box
                key={`term.${idx}`}
                mb={1}
                style={{
                  padding: 10,
                  borderRadius: 12,
                  border: "1px solid rgba(15,59,98,0.12)",
                  background: "linear-gradient(145deg, #ffffff 0%, #f6f9fd 100%)",
                }}
              >
                <Typography variant="subtitle2" style={{ fontWeight: 800, color: "#0f3b62" }}>
                  {t?.title || `Schedule ${idx + 1}`}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {isFilled(t?.fromDate) || isFilled(t?.toDate)
                    ? `From ${formatHandbookDateDisplay(t?.fromDate) || "--"} to ${
                        formatHandbookDateDisplay(t?.toDate) || "--"
                      }`
                    : "Dates not set"}
                </Typography>
              </Box>
            ))}
        </Box>
      ) : isStringList ? (
        <Box mt={1}>
          {value.filter((item) => isFilled(item)).map((item, idx) => (
            <Typography key={`list-item-${idx}`} variant="body2" color="textSecondary" style={{ marginBottom: 4 }}>
              {`${idx + 1}. ${item}`}
            </Typography>
          ))}
        </Box>
      ) : isImageUrl ? (
        <Box mt={1}>
          <img
            src={value}
            alt={label}
            style={{
              width: "100%",
              maxWidth: 360,
              height: 200,
              objectFit: "cover",
              borderRadius: 12,
              border: "1px solid rgba(15,59,98,0.15)",
            }}
          />
        </Box>
      ) : (
        <Typography variant="body2" color="textSecondary" style={{ whiteSpace: "pre-wrap" }}>
          {fieldKey && HANDBOOK_DATE_FIELD_KEYS.has(fieldKey)
            ? formatHandbookDateDisplay(value) || String(value)
            : String(value)}
        </Typography>
      )}
    </Box>
  );
}

export default function ParentHandbook() {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const sections = useMemo(
    () => [
      {
        key: "schoolOverview",
        title: "School Overview",
        fields: [
          ["visionMissionCoreValues", "Vision, mission, and core values"],
          ["briefHistory", "Brief history of the school"],
          ["principalMessage", "Message from the principal"],
          ["principalImageUrl", "Principal photo"],
          ["yearOfEstablishment", "Year of establishment"],
          ["branchesIfAny", "Branches (if any)"],
        ],
      },
      {
        key: "dailySchedule",
        title: "Daily Schedule",
        fields: [
          ["arrivalTime", "Arrival time"],
          ["dismissalTime", "Dismissal time"],
          ["breaksTime", "Breaks time"],
          ["assemblyDetails", "Assembly details"],
          ["lateArrivalEarlyLeaveProcedures", "Late arrival & early leave procedures"],
        ],
      },
      {
        key: "academicInformation",
        title: "Academic Information",
        fields: [
          ["academicCalendar", "Academic calendar"],
          ["examTerms", "Exam schedule"],
          ["winterVacationFrom", "Winter vacation from"],
          ["winterVacationTo", "Winter vacation to"],
          ["summerVacationFrom", "Summer vacation from"],
          ["summerVacationTo", "Summer vacation to"],
        ],
      },
      {
        key: "rulesAndConduct",
        title: "School Rules & Code of Conduct",
        fields: [
          ["disciplinePoliciesConsequences", "Discipline policies & consequences"],
          ["mobilePhonesAndDevicesPolicy", "Use of mobile phones & electronic devices"],
        ],
      },
      {
        key: "uniformAndDressCode",
        title: "Uniform & Dress Code",
        fields: [
          ["dailyUniformRequirements", "Daily uniform requirements (if any)"],
          ["specialOccasionsGuidelines", "Guidelines for special occasions"],
        ],
      },
      {
        key: "transportation",
        title: "Transportation",
        fields: [
          ["busRoutesAndRules", "Bus routes & rules"],
          ["busStops", "Route stops"],
          ["safetyGuidelines", "Safety guidelines"],
          ["transportContactDetails", "Transport contact details"],
        ],
      },
      {
        key: "healthAndSafety",
        title: "Health & Safety",
        fields: [
          ["medicalFacilitiesFirstAid", "Medical facilities & first aid"],
          ["emergencyProcedures", "Emergency procedures"],
          ["illnessGuidelinesAbsence", "Illness guidelines & absence"],
          ["vaccinationRequirements", "Vaccination requirements (if applicable)"],
        ],
      },
      {
        key: "libraryAndResources",
        title: "Library & Learning Resources",
        fields: [
          ["libraryRulesAndTimings", "Library rules & timings"],
          ["libraryOpenTime", "Library open time"],
          ["libraryCloseTime", "Library close time"],
          ["digitalResourcesUse", "Use of digital resources"],
          ["bookBorrowingPolicies", "Book borrowing policies"],
        ],
      },
      {
        key: "extracurricular",
        title: "Co-curricular & Extracurricular",
        fields: [
          ["clubsSportsCompetitions", "Clubs, sports and competitions"],
          ["annualEventsCelebrations", "Annual events and celebrations"],
        ],
      },
      {
        key: "parentInvolvement",
        title: "Parent Involvement",
        fields: [
          ["parentTeacherMeetings", "Parent-teacher meetings (PTMs)"],
          ["communicationChannels", "Communication channels"],
        ],
      },
      {
        key: "feesAndFinancial",
        title: "Fees & Financial Information",
        fields: [
          ["feeStructurePaymentMethods", "Fee structure and payment methods"],
          ["feeAmount", "Fee structure amount"],
          ["paymentScheduleDate", "Payment schedule date"],
          ["refundPolicy", "Refund policy"],
          ["scholarshipsConcessions", "Scholarships or concessions"],
        ],
      },
      {
        key: "importantContacts",
        title: "Important Contacts",
        fields: [
          ["schoolOfficeContactDetails", "School office contact details"],
          ["keyStaffMembers", "Key staff members"],
          ["emergencyContacts", "Emergency contacts"],
        ],
      },
      {
        key: "policiesAndAgreements",
        title: "Policies & Agreements",
        fields: [
          ["attendancePolicy", "Attendance policy"],
          ["leaveApplicationProcedures", "Leave application procedures"],
          ["internetUsagePolicy", "Internet usage policy"],
          ["declarationFormText", "Declaration form text"],
        ],
      },
    ],
    []
  );

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    HandbookService.getParent()
      .then((res) => {
        if (!mounted) return;
        setDoc(res || null);
        setHasError(false);
      })
      .catch(() => {
        if (!mounted) return;
        setDoc(null);
        setHasError(true);
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <>
      <PageTitle title="Hand Book" />
      {loading ? (
        <Box display="flex" alignItems="center">
          <CircularProgress size={20} style={{ marginRight: 10 }} />
          <Typography variant="body2" color="textSecondary">
            Loading handbook...
          </Typography>
        </Box>
      ) : null}

      {!loading && (!doc || hasError) ? (
        <Box mt={2}>
          <Typography variant="body2" color="textSecondary">
            {hasError ? "Unable to load handbook right now." : "No handbook details added by the school yet."}
          </Typography>
        </Box>
      ) : null}

      {doc ? (
        <Grid container spacing={3}>
          {sections.map((s) => {
            const sectionData = doc?.[s.key] || {};
            const hasAny = s.fields.some(([k]) => isFilled(sectionData?.[k]));
            if (!hasAny) return null;
            return (
              <Grid item xs={12} md={8} key={s.key}>
                <Card style={{ borderRadius: 14, border: "1px solid rgba(15,59,98,0.12)" }}>
                  <CardContent>
                    <Typography variant="h6" style={{ fontWeight: 800, color: "#0f3b62" }} gutterBottom>
                      {s.title}
                    </Typography>
                    {s.fields.map(([k, label]) => (
                      <Field key={k} fieldKey={k} label={label} value={sectionData?.[k]} />
                    ))}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      ) : null}
    </>
  );
}

